/**
 * Thinking-tab utilities: tool display name formatting, persisted-history
 * parsing, and live/persisted timeline merging.
 */

import { AgentSystemEvent } from "../../redux/reducers/Execution";

/** Map of known tool function names to human-friendly display names. */
export const TOOL_DISPLAY_NAMES: Record<string, string> = {
  editor: "Editor",
  diagram: "Diagram",
  web_search_with_content: "Web Search",
  read_asset: "Read Asset",
  read_uploaded_file: "Reading File",
  retrieve_indexed_content: "Reading File",
  project_file_list: "File List",
  assign_project_name: "Project Name",
  get_aws_icon: "AWS Icon",
  list_aws_icons: "List Icons",
  get_events: "Calendar Events",
  get_meeting_details: "Meeting Details",
  get_account: "Account Details",
  get_opportunity: "Opportunity Details",
  search_documentation: "AWS Docs Search",
  read_documentation: "Read Docs",
  recommend: "AWS Recommend",
};

/** Known tool name keys from the TOOL_DISPLAY_NAMES map. */
export type KnownToolName = keyof typeof TOOL_DISPLAY_NAMES;

/** MCP-style tool names follow the pattern "namespace___tool_name". */
type McpToolName = `${string}___${KnownToolName}`;

/** All valid tool name formats the formatter accepts. */
export type ToolName = KnownToolName | McpToolName | string;

/**
 * Formats a raw tool name into a human-friendly display name.
 *
 * Resolution order:
 * 1. Known tools — exact match in TOOL_DISPLAY_NAMES
 * 2. MCP tools — strips "namespace___" prefix, re-checks known tools, then Title Case
 * 3. Unknown tools — converts snake_case to Title Case
 *
 * @param toolName - Raw tool name from the agent (e.g., "editor", "aws___search_documentation")
 * @returns Human-friendly display name (e.g., "Editor", "AWS Docs Search")
 */
export function formatToolDisplayName(toolName: ToolName | undefined): string {
  if (!toolName) return "Unknown";

  // Check known tools first (exact match)
  if (TOOL_DISPLAY_NAMES[toolName]) return TOOL_DISPLAY_NAMES[toolName];

  // Handle MCP-style names: "namespace___tool_name" → strip prefix
  let baseName = toolName;
  if (toolName.includes("___")) {
    baseName = toolName.split("___").pop() || toolName;
  }

  // Re-check known tools with the stripped base name
  if (TOOL_DISPLAY_NAMES[baseName]) return TOOL_DISPLAY_NAMES[baseName];

  // Convert snake_case to Title Case
  return baseName
    .replace(/_/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Parsed S3 thinking content: structured events + optional legacy markdown. */
export interface PersistedThinkingParseResult {
  events: AgentSystemEvent[];
  legacyMarkdown: string | undefined;
}

/**
 * Parse persisted thinking content from S3. The JSON-event format uses
 * snake_case keys; we normalize to camelCase `AgentSystemEvent`. Non-JSON
 * content is returned as `legacyMarkdown` for the caller to render directly.
 */
export function parsePersistedThinkingEvents(
  content: string | undefined,
): PersistedThinkingParseResult {
  if (!content) {
    return { events: [], legacyMarkdown: undefined };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    // Not JSON — fall back to legacy markdown rendering.
    return { events: [], legacyMarkdown: content };
  }

  if (!Array.isArray(parsed)) {
    // JSON, but not the expected event-array shape — treat as legacy.
    return { events: [], legacyMarkdown: content };
  }

  const events: AgentSystemEvent[] = parsed.map(
    (raw: Record<string, unknown>) => ({
      eventType: (raw.event_type as string) || "unknown",
      toolName: (raw.tool_name as string) || undefined,
      details: (raw.details as string) || undefined,
      timestamp: (raw.timestamp as string) || new Date().toISOString(),
    }),
  );

  return { events, legacyMarkdown: undefined };
}

/** Identity key for dedup across persisted + live events. */
export function eventKey(e: AgentSystemEvent): string {
  return `${e.timestamp}|${e.eventType}|${e.toolName ?? ""}|${e.details ?? ""}`;
}

/**
 * Merge persisted history with live events into one chronological timeline.
 * Persisted first, live appended; duplicates (same timestamp + eventType +
 * toolName + details) are dropped so re-streamed events don't double-render.
 */

export function mergeThinkingEvents(
  persistedEvents: AgentSystemEvent[],
  liveEvents: AgentSystemEvent[],
): AgentSystemEvent[] {
  if (persistedEvents.length === 0) return liveEvents;
  if (liveEvents.length === 0) return persistedEvents;

  const seen = new Set<string>();
  const merged: AgentSystemEvent[] = [];

  for (const event of persistedEvents) {
    const key = eventKey(event);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(event);
  }

  for (const event of liveEvents) {
    const key = eventKey(event);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(event);
  }

  return merged;
}

/** A grouped event with an optional repeat count for consecutive same-tool calls. */
export interface GroupedEvent {
  event: AgentSystemEvent;
  repeatCount?: number;
}

/**
 * Groups consecutive same-tool `tool_start` events and filters out the
 * `tool_complete`/`tool_input` events belonging to collapsed groups. Returns a
 * flat list suitable for rendering.
 *
 * Lives here rather than in a component because it is pure and is shared by
 * every surface that renders an agent activity timeline.
 */
export function groupEvents(events: AgentSystemEvent[]): GroupedEvent[] {
  const grouped: GroupedEvent[] = [];
  let i = 0;
  while (i < events.length) {
    const event = events[i];

    if (event.eventType === "tool_complete") {
      i++;
      continue;
    }

    if (event.eventType === "tool_start") {
      let count = 1;
      while (
        i + count < events.length &&
        events[i + count].eventType === "tool_start" &&
        events[i + count].toolName === event.toolName
      ) {
        count++;
      }
      grouped.push({
        event:
          count > 1 ? { ...event, details: event.details || undefined } : event,
        repeatCount: count > 1 ? count : undefined,
      });
      i += count;
      // Skip tool_input events that belong to this collapsed group
      while (
        i < events.length &&
        events[i].eventType === "tool_input" &&
        events[i].toolName === event.toolName &&
        count > 1
      ) {
        i++;
      }
      continue;
    }

    grouped.push({ event });
    i++;
  }
  return grouped;
}
